import { z } from 'zod'

export const borderColor = ({
	first,
	crossover,
	last,
}: {
	first?: boolean | null
	crossover?: boolean | null
	last?: boolean | null
}) => {
	let output = ''
	// overwrite blue with green - prioritizing green
	if (crossover) output = 'rounded-bottom border-t-4 border-t-blue-900/70 '
	if (first) output = 'border-t-4 border-t-green-900/70 '
	// append bottom
	if (last) output += 'border-b-4 border-b-red-900/70 '
	return output
}

export const backgroundColor = (charge: string) => {
	switch (charge) {
		case 'first':
			return 'bg-green-900 border border-secondary-foreground font-semibold'
		case 'crossover':
			return 'bg-blue-900 border border-secondary-foreground font-semibold'
		case 'last':
			return 'bg-red-900 border border-secondary-foreground font-semibold'
		case 'normal':
			return 'bg-muted border border-secondary-foreground/40'
		case 'none':
		default:
			return 'bg-muted/40'
	}
}

export const SearchResultsSchema = z.array(
	z.object({
		userId: z.string(),
		quickbooks: z.string().optional(),
		display: z.string(),
		primaryEmail: z.string().optional().nullable(),
		primaryOptIn: z.boolean().optional().nullable(),
		portId: z.string(),
		ditch: z.preprocess(
			(x) => (x ? x : undefined),
			z.coerce.number().int().min(1).max(9),
		),
		position: z.preprocess(
			(x) => (x ? x : undefined),
			z.coerce.number().int().min(1).max(99),
		),
		entry: z.string(),
		address: z.number().nullable(),
		section: z.string(),
		hours: z.preprocess(
			(x) => (x ? x : 0),
			z.coerce.number().multipleOf(0.5).min(0).max(36),
		),
		start: z.date().nullable(),
		stop: z.date().nullable(),
		first: z.boolean().optional().nullable(),
		crossover: z.boolean().optional().nullable(),
		last: z.boolean().optional().nullable(),
		emailed: z.boolean().optional().nullable(),
	}),
)
type FirstDitchType = {
	// ditch
	[key: string]: {
		// entry - for <td>
		[key: string]: {
			// section
			[key: string]: boolean
		}
	}
}

export type UserScheduleType = {
	timelineId?: string
	userId: string
	portId: string
	display: string | null
	primaryEmail?: string | null
	primaryOptIn?: boolean | null
	ditch: number
	position: number
	entry: string
	section: string
	address: number | null
	hours: number | bigint | null
	start: Date | string | null
	stop: Date | string | null
	userSchedule?: string[]
	ditchTime?: number
	first?: boolean | null
	crossover?: boolean | null
	last?: boolean | null
	emailed?: boolean | null
	duties?: DutyType[]
}
export type DutyType = {
	crossoverId: string
	duty: string
	dutyStartString: string
	dutyDetail: string
	acknowledged?: boolean | null
	requestsTraining?: boolean | null
	member?: string | null
	volunteer?: string | null
	isCurrentUser?: boolean
	isCurrentVolunteer?: boolean
	isLocked?: boolean
}
export const assignDutiesToSchedules = (schedules: UserScheduleType[]) => {
	const found: FirstDitchType = {
		'1': { '10-01': { first: false, South: false, last: false } },
		'2': { '10-01': { first: false, South: false, last: false } },
		'3': { '10-01': { first: false, South: false, last: false } },
		'4': { '10-01': { first: false, South: false, last: false } },
		'5': { '10-03': { first: false, South: false, last: false } },
		'6': { '10-03': { first: false, South: false, last: false } },
		'7': { '10-03': { first: false, South: false, last: false } },
		'8': { '10-03': { first: false, South: false, last: false } },
		'9': {
			'10-01': { first: false, East: false, last: false },
			'10-03': { first: false, East: false, last: false },
		},
	}

	const updated = schedules.map((schedule) => {
		const { ditch, entry, section, hours } = schedule
		if (!ditch || !entry) return
		if (hours) {
			if (
				found[ditch] &&
				found[ditch][entry] &&
				found[ditch][entry].first === false
			) {
				found[ditch][entry].first = true
				schedule.first = true
			}

			if (
				found[ditch] &&
				found[ditch][entry] &&
				found[ditch][entry][section] === false
			) {
				found[ditch][entry][section] = true
				schedule.crossover = true
			}
		}
		return schedule
	})

	return updated.reverse().map((schedule) => {
		if (!schedule) return
		const { hours, ditch, entry } = schedule
		if (hours && found[ditch] && found[ditch][entry]) {
			if (found[ditch][entry].last === false) {
				found[ditch][entry].last = true
				schedule.last = true
			}
		}
		return schedule
	})
}

const last = 'Please do not pull your checks after irrigation.'
export const crossoverDuties: {
	[key: string]: { [key: string]: { [key: string]: string } }
} = {
	'1': {
		'10-01': {
			first: 'Ditch 9 to Ditch 1 (Open Ditch 1, then Close Ditch 9)',
			crossover:
				'Crossover @ Ditch 1 and Orangewood (Take Debris to a Dumpster)',
			last,
		},
	},
	'2': {
		'10-01': {
			first: 'Ditch 1 to Ditch 2 (Open Ditch 2, then Close Ditch 1)',
			crossover:
				'Crossover @ Ditch 2 and Orangewood (Take Debris to a Dumpster)',
			last,
		},
	},
	'3': {
		'10-01': {
			first: 'Ditch 2 to Ditch 3 (Open Ditch 3, then Close Ditch 2)',
			crossover:
				'Crossover @ Ditch 3 and Orangewood (Take Debris to a Dumpster)',
			last,
		},
	},
	'4': {
		'10-01': {
			first: 'Ditch 3 to Ditch 4 (Open Ditch 4, then Close Ditch 3)',
			crossover:
				'Crossover @ Ditch 4 and Orangewood (Take Debris to a Dumpster)',
			last,
		},
	},
	'5': {
		'10-03': {
			first: 'Ditch 9 to Ditch 5 (Open Ditch 5, then Close Ditch 9)',
			crossover:
				'Crossover @ Ditch 5 and Orangewood (Take Debris to a Dumpster)',
			last,
		},
	},
	'6': {
		'10-03': {
			first: 'Ditch 5 to Ditch 6 (Open Ditch 6, then Close Ditch 5)',
			crossover:
				'Crossover @ Ditch 6 and Orangewood (Take Debris to a Dumpster)',
			last,
		},
	},
	'7': {
		'10-03': {
			first: 'Ditch 6 to Ditch 7 (Open Ditch 7, then Close Ditch 6)',
			crossover:
				'Crossover @ Ditch 7 and Orangewood (Take Debris to a Dumpster)',
			last,
		},
	},
	'8': {
		'10-03': {
			first: 'Ditch 7 to Ditch 8 (Open Ditch 8, then Close Ditch 7)',
			crossover:
				'Crossover @ Ditch 8 and Orangewood (Take Debris to a Dumpster)',
			last,
		},
	},
	'9': {
		'10-01': {
			first: '10-01 starts (Open Ditch 9, Close Ditch 4)',
			crossover: 'Crossover @ Ditch 9 and 185th (Take Debris to a Dumpster)',
			last,
			connection: 'Crossover @ Ditch 9 and 183th (Take Debris to a Dumpster)',
		},
		'10-03': {
			first: '10-03 starts (Open Ditch 9)',
			crossover: 'Crossover @ Ditch 9 and 181st (Take Debris to a Dumpster)',
			last,
		},
	},
}

export const crossover: {
	[key: string]: {
		[key: string]: { [key: string]: { first: boolean; description: string } }
	}
} = {
	'1': {
		'10-01': {
			North: {
				first: false,
				description: 'Ditch 9 to Ditch 1 (Open Ditch 1, then Close Ditch 9)',
			},
			South: {
				first: false,
				description:
					'Crossover @ Ditch 1 and Orangewood (Take Debris to a Dumpster)',
			},
		},
	},
	'2': {
		'10-01': {
			North: {
				first: false,
				description: 'Ditch 1 to Ditch 2 (Open Ditch 2, then Close Ditch 1)',
			},
			South: {
				first: false,
				description:
					'Crossover @ Ditch 2 and Orangewood (Take Debris to a Dumpster)',
			},
		},
	},
	'3': {
		'10-01': {
			North: {
				first: false,
				description: 'Ditch 2 to Ditch 3 (Open Ditch 3, then Close Ditch 2)',
			},
			South: {
				first: false,
				description:
					'Crossover @ Ditch 3 and Orangewood (Take Debris to a Dumpster)',
			},
		},
	},
	'4': {
		'10-01': {
			North: {
				first: false,
				description: 'Ditch 3 to Ditch 4 (Open Ditch 4, then Close Ditch 3)',
			},
			South: {
				first: false,
				description:
					'Crossover @ Ditch 4 and Orangewood (Take Debris to a Dumpster)',
			},
		},
	},
	'5': {
		'10-03': {
			North: {
				first: false,
				description: 'Ditch 9 to Ditch 5 (Open Ditch 5, then Close Ditch 9)',
			},
			South: {
				first: false,
				description:
					'Crossover @ Ditch 5 and Orangewood (Take Debris to a Dumpster)',
			},
		},
	},
	'6': {
		'10-03': {
			North: {
				first: false,
				description: 'Ditch 5 to Ditch 6 (Open Ditch 6, then Close Ditch 5)',
			},
			South: {
				first: false,
				description:
					'Crossover @ Ditch 6 and Orangewood (Take Debris to a Dumpster)',
			},
		},
	},
	'7': {
		'10-03': {
			North: {
				first: false,
				description: 'Ditch 6 at Ditch 7 (Open Ditch 7, then Close Ditch 6)',
			},
			South: {
				first: false,
				description:
					'Crossover @ Ditch 7 and Orangewood (Take Debris to a Dumpster)',
			},
		},
	},
	'8': {
		'10-03': {
			North: {
				first: false,
				description: 'Ditch 7 to Ditch 8 (Open Ditch 8, then Close Ditch 7)',
			},
			South: {
				first: false,
				description:
					'Crossover @ Ditch 8 and Orangewood (Take Debris to a Dumpster)',
			},
		},
	},
	'9': {
		'10-01': {
			West: {
				first: false,
				description: '10-01 starts (Open Ditch 9)',
			},
			East: {
				first: false,
				description:
					'Crossover @ Ditch 9 and 185th (Take Debris to a Dumpster)',
			},
		},
		'10-03': {
			West: {
				first: false,
				description: '10-03 starts (Open Ditch 9)',
			},
			East: {
				first: false,
				description:
					'Crossover @ Ditch 9 and 181st (Take Debris to a Dumpster)',
			},
		},
	},
}
